import styled from '@emotion/styled';
import { ConditionalPropResponse, Episode } from '../../apiService';
import { __t } from '../../i18n';
import Reaction from '../../Reaction';
import Rating from '../../Rating';
import { Card, Text, Box, Flex } from '../../styles';
import { durationToWords, getContentUrl, getTimerDuration, getWidgetsTeaserPath, inspectFlags, usePlayerService } from '../../utils';
import { SpacingProps } from '../../withStyleSystem';
import useConditionalProps from '../../utils/useConditionalProps';
import CountdownTimer from '../../CountdownTimer';
import CardBadge from '../../CardBadge';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import PlayButton from '../../PlayButton';
import Mp4Player from '../Widgets/WidgetManager/WidgetsPlayer/Mp4Player';
import HlsPlayer from '../Widgets/WidgetManager/WidgetsPlayer/HlsPlayer';
import DefaultPreviewButtons from '../Widgets/WidgetManager/WidgetsPlayer/DefaultPreviewButtons';
import DesktopOnlyWrapper from '../../styles/DesktopOnlyWrapper';
import useDelayedHover from '../../useDelayedHover';
import usePauseHeroVideo from '../../utils/usePauseHeroVideo';
import { VideoTrack } from '../Player/PlayerModels';

export default function EpisodeItem(props: EpisodeItemProps) {
  const {
    id,
    duration,
    rate_percentage,
    flag,
    conditional_flag,
    coming_soon,
    short_id,
    active = false,
    video_files,
    onHoverChange,
  } = props;
  const { selected } = inspectFlags(conditional_flag);
  const teaserPath = getWidgetsTeaserPath(video_files, id);
  const [hovered, setHovered] = useState(false);
  const { pauseHero, resumeHero } = usePauseHeroVideo();

  const delayedHover = useDelayedHover((isHovered) => {
    setHovered(isHovered);
    onHoverChange?.(isHovered ? id : null);
  });

  const onMouseEnter = () => {
    if (typeof window !== 'undefined' && window.innerWidth <= Number(1200)) return;
    pauseHero();
    delayedHover(true);
  };

  const onMouseLeave = () => {
    delayedHover(false);
    resumeHero();
  };

  return (
    <CardContainer
      active={active}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <Container active={selected} direction="column" p={{ md: 1, lg: 2 }}>
        <EpisodeBody
          {...props}
          teaserPath={teaserPath}
          previewActive={active}
          previewMounted={hovered}
        />
        <EpisodeFooter {...{ id, duration, rate_percentage, flag, coming_soon, short_id }} />
      </Container>
    </CardContainer>
  );
}

// -------------- //
// SUB-COMPONENTS //
// -------------- //

const EpisodeBody = ({ short_id, slug, episode, rate_percentage,
  sub_title, conditional_flag, cover_image, flag, coming_soon, teaserPath, previewActive, previewMounted }: EpisodeBodyProps) => {
  const url = getContentUrl({ short_id, slug });
  const thumbProps: EpisodeThumbProps = { cover_image, conditional_flag, flag,
    id: short_id, coming_soon, short_id, slug, teaserPath, previewActive, previewMounted };
  const infoProps: EpisodeInfoProps = { url, episode, rate_percentage, sub_title, coming_soon, flag, previewActive };
  return (
    <Flex
      direction={{ md: 'column' }}
      mb={1}
    >
      <EpisodeThumb {...thumbProps} />
      <EpisodeInfo {...infoProps} py={1} />
    </Flex>
  );
};

const EpisodeThumb = ({ conditional_flag, cover_image,
  flag, id, short_id, slug, teaserPath, previewActive, previewMounted }: EpisodeThumbProps) => {
  const flags = [conditional_flag, flag].join(', ');
  const { playable, free, comingSoon, isCinema } = inspectFlags(flags);
  const router = useRouter();
  // The episode's flags are anonymous, so it goes by the viewer's access to the content, as the
  // content page's own play button does.
  const hasAccess = useConditionalProps((s) => s.hasAccess);
  const [handleWatch, { loading }] = usePlayerService({ id, free, isCinema, hasAccess });

  const url = `/contents/${short_id ?? id}/${slug}`;
  const canPlay = playable && !comingSoon;
  const handleClick = () => {
    if (canPlay) {
      handleWatch('p');
    } else if (router.asPath !== url) {
      router.push(url);
    }
  };

  const image = cover_image?.path;
  const poster = image ? `${image}?w=300` : undefined;

  return (
    <CardWrapper ml={{ xs: 2, md: 0 }} mb={{ md: 2, xs: 0.5 }} cursor={router.asPath !== url ? 'pointer' : 'auto'}>
      <ThumbCard
        data-has-preview={teaserPath ? 'true' : 'false'}
        onClick={handleClick}
        orientation="horizontal"
        backgroundImage={`url(${image}?w=330)`}
      >
        {flags.length > 0 && !comingSoon &&  <CardBadge flag={flag} data-badge={teaserPath ? 'true' : 'false'}/>}
        <DesktopOnlyWrapper>
          {previewMounted && teaserPath?.src && (
            teaserPath.type === 'mp4' ? (
              <Mp4Player
                src={teaserPath.src}
                poster={poster}
                active={previewActive}
                loop
                autoplay
              >
                {(api) => <DefaultPreviewButtons api={api} showFullscreen={false} />}
              </Mp4Player>
            ) : (
              <HlsPlayer
                source={teaserPath}
                poster={poster}
                active={previewActive}
                loop
                autoplay
              >
                {(api) => <DefaultPreviewButtons api={api} showFullscreen={false} />}
              </HlsPlayer>
            )
          )}
        </DesktopOnlyWrapper>
      </ThumbCard>
      {!(comingSoon || !playable) &&
       <DesktopOnlyWrapper>
         <ButtonWrapper onClick={handleClick} style={{ opacity: loading ? 1 : undefined }}>
           <PlayButton loading={loading} />
         </ButtonWrapper>
       </DesktopOnlyWrapper>}
    </CardWrapper>
  );
};

const EpisodeInfo = ({ episode, rate_percentage, sub_title, url,
  coming_soon, flag, previewActive,  ...spacing }: EpisodeInfoProps & SpacingProps) => {
  const router = useRouter();

  const comingSoonFlag = flag.includes('coming_soon');
  const handleClickOnInfoBox = () => {
    if (router.asPath !== url) router.push(url);
  };

  return <InfoBox onClick={handleClickOnInfoBox} {...spacing} clickable={router.asPath !== url}>
    <Flex justify="space-between" direction={{ xs: 'column', sm: 'column', md: 'row' }}>
      {previewActive && episode <= 0 ? '' : (
        episode <= 0 && sub_title ? (
          sub_title
        ) : (
          <EpisodeNumber number={episode} />
        )
      )}
      {!comingSoonFlag && rate_percentage && <Flex mr={'auto'}><Rating rate={rate_percentage} mr={2} className="desktop-only" /></Flex>}
      {comingSoonFlag && <ComingSoonBadge display='block' responsiveDisplay={coming_soon ? 'none' : 'block'} my={{ xs: '0.5rem', sm: '0.5rem', md: '0rem'  }}>{__t('common.soon')}</ComingSoonBadge>}
    </Flex>

    <SubtitleText
      variant="body3"
      color={previewActive && episode <= 0 ? 'default' : 'weak'}
      weight="medium"
    >
      {previewActive && episode <= 0 ? sub_title : episode > 0 ? sub_title : null}
    </SubtitleText>

  </InfoBox>;
};

const EpisodeNumber = ({ number }: { number?: number }) => (
  <Text variant="body1" weight="medium">
    {__t('content.type.episode')} <b>{number}</b>
  </Text>
);

const EpisodeFooter = ({ duration, id, rate_percentage, flag, coming_soon, short_id }: EpisodeFooterProps) => {
  const durationWords = duration && durationToWords(duration, true);
  const serverTime = useConditionalProps((s) => s.serverTime);
  const [timer, setTimer] = useState<number>(0);
  useEffect(() => {
    (serverTime && coming_soon) && setTimer(Number(getTimerDuration(coming_soon, serverTime)));
  }, [coming_soon, serverTime]);

  const comingSoonFlag = flag?.includes('coming_soon');

  return (
    <Flex justify="space-between" align="baseline" direction={{ md: 'row-reverse' }} mt={{ xs: '0.25rem' }}>
      {timer && comingSoonFlag
        ? <Flex justify="space-between" align="baseline" direction={{ md: 'row-reverse' }} fullWidth>
          <CountdownTimer
            timeDigitsWidth='1.7rem'
            value={Number(timer)}
            separatorPaddingY="0.25rem"
            containerCustomPadding="0.25rem"
            containerCustomRadius="0.25rem"
          />
          {comingSoonFlag &&  <ComingSoonBadge display="none">{__t('common.soon')}</ComingSoonBadge>}
        </Flex>
        : <ReactionWrapper havePadding>
          {!comingSoonFlag &&  <Reaction type="content" flag={flag || ''} id={short_id || id} inputSize="small" />}
        </ReactionWrapper>
      }
      <Box>{durationWords && !comingSoonFlag && <EpisodeDurationText color="weak" as="span" variant="body3" notShow={!!coming_soon}>
        {durationWords}
      </EpisodeDurationText>
      }
      {rate_percentage && !comingSoonFlag &&  <Rating rate={rate_percentage} mr={2} className="mobile-only" />}
      </Box>
    </Flex>
  );
};

// ------ //
// STYLES //
// ------ //

const Container = styled(Flex)<{ active: boolean }>(({ theme }) => ({
  [theme.breakpoints.up('md')]: {
    height: '100%',
    backgroundColor: theme.palette.background[700][0],
    borderRadius: theme.radius.medium,
    transition: 'background-color .3s ease',
  },
}));

const SubtitleText = styled(Text)(({ theme }) => ({
  [theme.breakpoints.up('md')]: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    minHeight: '1.5em',
  },
}));

const ThumbCard = styled(Card)<{backgroundImage : string}>(({ backgroundImage }) => ({
  backgroundImage,
  position: 'relative',
}));

const InfoBox = styled(Box)<{ clickable: boolean }>(({ clickable }) => ({
  position: 'relative',
  zIndex: 2,
  flexGrow: 1,
  cursor: clickable ? 'pointer' : 'auto',
}));

const CardWrapper = styled(Box)<{ cursor?: 'pointer' | 'auto' }>(({ theme, cursor = 'pointer' }) => ({
  position: 'relative',
  overflow: 'visible',
  cursor,
  width: '50%',
  minWidth: '6.125rem',
  flexShrink: 0,
  [`${Card}`]: {
    borderRadius: theme.radius.default,
  },
  [theme.breakpoints.up('md')]: {
    width: '100%',
  },
}));

const ComingSoonBadge = styled(Flex)<{display?: 'block' | 'none', responsiveDisplay?:  'block' | 'none' }>(({ theme, display, responsiveDisplay }) => ({
  width: '2.87rem',
  height: '1.5rem',
  backgroundColor: theme.palette.warning[900][1],
  color: theme.palette.text[900][0],
  borderRadius: '6.25rem',
  fontSize: '0.75rem',
  alignItems: 'center',
  justifyContent: 'center',
  weight: '500',
  padding: '0.25rem 0.5rem',
  [theme.breakpoints.up('md')]: {
    display,
  },
  [theme.breakpoints.down('md')]: {
    display: responsiveDisplay,
  },
}));

const EpisodeDurationText = styled(Text)<{ notShow?: boolean }>(({ theme, notShow }) => ({
  [theme.breakpoints.between('md', 1120)]: {
    display: notShow ? 'none' : undefined,
  },
}));

const ReactionWrapper = styled.div<{ havePadding?: boolean }>(({ theme, havePadding }) => ({
  [theme.breakpoints.between('sm', 'md')]: {
    paddingTop: havePadding ? '0.32rem' : undefined,
  },
  [theme.breakpoints.up('md')]: {
    position: 'relative',
    zIndex: 6,
    pointerEvents: 'auto',
  },
  minHeight: '30px',
}));

const ButtonWrapper = styled.div(({ theme }) => ({
  position: 'absolute',
  left: '50%',
  bottom: '-2rem',
  width: theme.spacing(7),
  height: theme.spacing(7),
  borderRadius: '30px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#fff',
  backgroundColor: theme.palette.background[700][0],
  transform: 'translateX(-50%)',
  transformOrigin: 'center',
  willChange: 'transform',
  opacity: 0,
  transition: 'opacity 180ms ease, transform 220ms ease',
  zIndex: 4,
  pointerEvents: 'auto',
  cursor: 'pointer',
  '& button': {
    width: theme.spacing(5),
    height: theme.spacing(5),
    minWidth: theme.spacing(5),
    padding: theme.spacing(1),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: 0,
    cursor: 'pointer',
    pointerEvents: 'none',
  },
  '& span': {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: 0,
    cursor: 'pointer',
  },
  '& svg': {
    width: theme.spacing(2),
    height: theme.spacing(2),
    display: 'block',
    transform: 'translate3d(1px,0,0)',
    willChange: 'transform',
    cursor: 'pointer',
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    inset: 6,
    borderRadius: '50%',
    background: '#b21f45',
    zIndex: -1,
  },
  [theme.breakpoints.down('sm')]: {
    display: 'none',
  },
}));

const CardContainer = styled.div<{ active: boolean }>(({ theme, active }) => {
  return {
    position: 'relative',
    height: '100%',
    zIndex: 1,
    transition: 'transform 550ms ease',
    transformOrigin: 'center',
    [theme.breakpoints.up('md')]: {
      ...(active && {
        transform: 'scale(1.25)',
        zIndex: 9999,
        [`& ${ThumbCard}[data-has-preview="true"]`]: {
          backgroundImage: 'none',
        },
        '& video': {
          opacity: 1,
        },
        '& [data-play-button="true"]': {
          opacity: 1,
          transform: 'translate(-50%, 0) scale(1)',
        },
        [`& ${ButtonWrapper}`]: {
          opacity: 1,
        },
        '& [data-player-controls="true"]': {
          opacity: 1,
          pointerEvents: 'auto',
        },
        '& [data-badge="true"]': {
          opacity: 0,
        },
      }),
    },
  };
});

// ----- //
// TYPES //
// ----- //

type EpisodeItemProps = Episode & {
  flags?: ConditionalPropResponse['conditional_flag'];
  active?: boolean;
  onHoverChange?: (id: string | null) => void;
};
type EpisodeBodyProps = Omit<EpisodeInfoProps & EpisodeThumbProps, 'url'> & Pick<Episode, 'short_id' | 'slug'>;
type EpisodeInfoProps = { url: string, previewActive?:boolean } & Pick<Episode, 'episode' | 'rate_percentage' | 'sub_title' | 'coming_soon' | 'flag'>;
type EpisodeThumbProps = Pick<Episode, 'conditional_flag' | 'cover_image' | 'flag' | 'id' | 'coming_soon' | 'slug' | 'short_id'> & {
  teaserPath?: VideoTrack;
  previewActive?: boolean;
  previewMounted?: boolean;
};
type EpisodeFooterProps = Pick<EpisodeItemProps, 'duration' | 'id' | 'rate_percentage' | 'flag' | 'coming_soon' | 'short_id'>;
